import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  migrateSite as migrateSiteService,
  getDeployments as getDeploymentsService,
  getDeploymentById as getDeploymentByIdService,
  rollbackDeployment as rollbackDeploymentService,
} from "../../services/Migration";

/**
 * Migrate site to target environment
 */
export const migrateSite = createAsyncThunk(
  "migration/migrateSite",
  async ({ siteId, payload }, { rejectWithValue }) => {
    try {
      const response = await migrateSiteService(siteId, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Migration failed"
      );
    }
  }
);

/**
 * Get deployments list
 */
export const getDeployments = createAsyncThunk(
  "migration/getDeployments",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getDeploymentsService(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to fetch deployments"
      );
    }
  }
);

/**
 * Get deployment by ID
 */
export const getDeploymentById = createAsyncThunk(
  "migration/getDeploymentById",
  async (deploymentId, { rejectWithValue }) => {
    try {
      const response = await getDeploymentByIdService(deploymentId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to fetch deployment"
      );
    }
  }
);

/**
 * Rollback deployment
 */
export const rollbackDeployment = createAsyncThunk(
  "migration/rollbackDeployment",
  async (deploymentId, { rejectWithValue }) => {
    try {
      const response = await rollbackDeploymentService(deploymentId);
      return { deploymentId, ...response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Rollback failed"
      );
    }
  }
);

const migrationSlice = createSlice({
  name: "migration",
  initialState: {
    // Migration state
    migrateLoading: false,
    migrateError: null,

    // Deployments list
    deployments: [],
    deploymentsLoading: false,
    deploymentsError: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      hasNext: false,
      hasPrev: false,
    },

    // Single deployment
    currentDeployment: null,
    deploymentLoading: false,
    deploymentError: null,

    // Rollback state
    rollbackLoading: false,
    rollbackError: null,
  },
  reducers: {
    clearMigrationError: (state) => {
      state.migrateError = null;
    },
    clearRollbackError: (state) => {
      state.rollbackError = null;
    },
    clearDeploymentsError: (state) => {
      state.deploymentsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Migrate Site
      .addCase(migrateSite.pending, (state) => {
        state.migrateLoading = true;
        state.migrateError = null;
      })
      .addCase(migrateSite.fulfilled, (state) => {
        state.migrateLoading = false;
        state.migrateError = null;
      })
      .addCase(migrateSite.rejected, (state, action) => {
        state.migrateLoading = false;
        state.migrateError =
          action.payload?.error || action.payload || "Migration failed";
      })

      // Get Deployments
      .addCase(getDeployments.pending, (state) => {
        state.deploymentsLoading = true;
        state.deploymentsError = null;
      })
      .addCase(getDeployments.fulfilled, (state, action) => {
        state.deploymentsLoading = false;
        state.deploymentsError = null;
        state.deployments = action.payload.deployments || [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(getDeployments.rejected, (state, action) => {
        state.deploymentsLoading = false;
        state.deploymentsError =
          action.payload?.error ||
          action.payload ||
          "Failed to fetch deployments";
      })

      // Get Deployment By ID
      .addCase(getDeploymentById.pending, (state) => {
        state.deploymentLoading = true;
        state.deploymentError = null;
      })
      .addCase(getDeploymentById.fulfilled, (state, action) => {
        state.deploymentLoading = false;
        state.deploymentError = null;
        state.currentDeployment = action.payload;
      })
      .addCase(getDeploymentById.rejected, (state, action) => {
        state.deploymentLoading = false;
        state.deploymentError =
          action.payload?.error ||
          action.payload ||
          "Failed to fetch deployment";
      })

      // Rollback Deployment
      .addCase(rollbackDeployment.pending, (state) => {
        state.rollbackLoading = true;
        state.rollbackError = null;
      })
      .addCase(rollbackDeployment.fulfilled, (state, action) => {
        state.rollbackLoading = false;
        state.rollbackError = null;

        // Update deployment status in list
        const index = state.deployments.findIndex(
          (d) => d.id === action.payload.deploymentId
        );
        if (index !== -1) {
          state.deployments[index].status = "ROLLED_BACK";
        }

        // Update current deployment if it's the one being rolled back
        if (state.currentDeployment?.id === action.payload.deploymentId) {
          state.currentDeployment.status = "ROLLED_BACK";
        }
      })
      .addCase(rollbackDeployment.rejected, (state, action) => {
        state.rollbackLoading = false;
        state.rollbackError =
          action.payload?.error || action.payload || "Rollback failed";
      });
  },
});

export const {
  clearMigrationError,
  clearRollbackError,
  clearDeploymentsError,
} = migrationSlice.actions;

export default migrationSlice.reducer;
